'use client'

import * as React from 'react'
import Link from 'next/link'
import { Github, Twitter, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ThankYouButton } from '@/components/ui'
import { useI18n } from '@/lib/i18n'

export function Footer() {
  const currentYear = new Date().getFullYear()
  const { t } = useI18n()
  const footerLinks = [
    { label: t('footer.about', 'About'), href: '/about' },
    { label: t('footer.privacy', 'Privacy'), href: '/privacy' },
    { label: t('footer.terms', 'Terms'), href: '/terms' },
  ]

  return (
    <footer className="w-full border-t border-border/40 bg-card/50">
      {/* Footer content */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: Copyright */}
          <p className="text-sm text-muted-foreground">
            © {currentYear} Roostr
          </p>

          {/* Center: Links */}
          <div className="flex items-center gap-4">
            {footerLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right: Thank you + Social */}
          <div className="flex items-center gap-4">
            <ThankYouButton />
            <div className="flex items-center gap-2">
              <SocialLink href="https://github.com" icon={Github} label="GitHub" />
              <SocialLink href="https://twitter.com" icon={Twitter} label={t('footer.twitter', 'Twitter')} />
              <SocialLink href="mailto:hello@roostr.me" icon={Mail} label={t('footer.email', 'Email')} />
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

interface SocialLinkProps {
  href: string
  icon: React.ElementType
  label: string
}

function SocialLink({ href, icon: Icon, label }: SocialLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center',
        'text-muted-foreground',
        'hover:bg-primary/10 hover:text-primary',
        'transition-colors'
      )}
      title={label}
    >
      <Icon className="h-4 w-4" />
    </a>
  )
}

